import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { type SearchResultMessagePartial } from '@atlassian/dynamic-cards-search-client';

import { MessageBody } from '../../../../../../../common/ui/message-body';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { getIntegrationKey } from '../../../../../../../common/utils/search-result-utils';
import { useBootstrap, usePrimaryProduct } from '../../../../../../../controllers/store';

import { Attachments } from './attachments';
import { i18n } from './messages';
import { ThreadBody } from './thread-body';
import { ThreadTitle } from './thread-title';

interface MessageSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultMessagePartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const MessageSearchResult = ({
	partial,
	position,
	icon,
}: MessageSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { url, linkedEntities } = partial;
	const integrationKey = getIntegrationKey(partial.integrationId);

	const labelMessage = linkedEntities?.length ? i18n.threadTypeLabel : i18n.messageTypeLabel;

	const title = <ThreadTitle partial={partial} />;
	const body = <ThreadBody partial={partial} components={{ Body: MessageBody }} />;
	const parts: Array<string | [string, React.ReactNode]> = [
		['label', <FormattedMessage {...labelMessage} />],
	];

	if (partial?.entity?.container?.membershipType === 'PRIVATE') {
		parts.push([
			'private',
			<LockLockedIcon
				label={formatMessage(i18n.privateChannelLabel)}
				color="var(--ds-icon)"
				size="small"
			/>,
		]);
	}

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={body}
			headerMetadata={<SearchMetadata product={integrationKey} productSelected parts={parts} />}
			footer={<Attachments attachments={partial.entity?.attachments} message={partial} />}
			title={title}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
